import { type Account, type RestoreAccount, type GCSBucket, Provider, type S3Bucket } from '@repo/api-gw-sdk';
import { useMemo } from 'react';
import { useDAL } from '@/contexts/useDAL';
import { getAccountProvider } from '@/utils/accounts';
import { isBucketInRegion, type NormalizedBucket } from './bucketMatching';
export type { NormalizedBucket } from './bucketMatching';
const normalizeBuckets = (buckets: GCSBucket[] | S3Bucket[] | undefined): NormalizedBucket[] => buckets?.map(bucket => ({
  bucketName: bucket.bucketName,
  regionName: 'location' in bucket ? bucket.location : bucket.regionName,
  locationType: 'locationType' in bucket ? bucket.locationType : undefined,
  constituentRegions: 'constituentRegions' in bucket ? bucket.constituentRegions : undefined
})) ?? [];
interface BucketsResult {
  buckets: NormalizedBucket[];
  isLoading: boolean;
  missingListPermissions: boolean;
}
export const useBuckets = (account?: Account | RestoreAccount, region?: string): BucketsResult => {
  const dal = useDAL();
  const cloudProvider = getAccountProvider(account);
  const accountId = account?.id;
  const bucketsResponse = {
    [Provider.Aws]: dal.restore.s3Buckets.useList(accountId, cloudProvider),
    [Provider.Gcp]: dal.restore.gcsBuckets.useList(accountId, cloudProvider),
    [Provider.Azure]: null,
    [Provider.MongoAtlas]: null,
    [Provider.GoogleWorkspace]: null,
    [Provider.Microsoft365]: null,
    [Provider.AzureEntraId]: null,
    [Provider.ProviderUnspecified]: null
  }[cloudProvider ?? Provider.ProviderUnspecified];
  const bucketsData = bucketsResponse?.data?.buckets;
  const isLoading = bucketsResponse?.isLoading ?? false;
  const missingListPermissions = bucketsResponse?.error?.code === 403;
  return useMemo(() => {
    const originBuckets = normalizeBuckets(bucketsData);
    return {
      isLoading,
      buckets: originBuckets.filter(bucket => !region || isBucketInRegion(bucket, region)),
      missingListPermissions
    };
  }, [bucketsData, isLoading, missingListPermissions, region]);
};